'use client';

import type { ReactNode } from 'react';

import clsx from 'clsx';
import { format, isToday } from 'date-fns';

import {
  type ChatMessage,
  type ClientChatMessage,
  MsgSendStatus,
} from '../../types/ChatMessage';

interface Props {
  message: ChatMessage;
  isOwn: boolean;
  /** When true, the shell adds no padding/background — the renderer
      paints its own (e.g. an image/video). */
  bare?: boolean;
  children: ReactNode;
}

/**
 * Shared layout for a chat bubble — alignment by direction, plus the
 * meta footer (timestamp + sending/failed indicator). The actual
 * bubble body comes in as `children`. Text bubbles paint their own
 * background + padding; media bubbles set `bare` so the shell stays
 * out of the way.
 */
export function MessageBubbleShell({ message, isOwn, bare, children }: Props) {
  const time = isToday(message.time)
    ? format(message.time, 'HH:mm')
    : format(message.time, 'MMM d, HH:mm');

  return (
    <div
      className={clsx(
        'flex w-full items-end gap-2',
        isOwn ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={clsx(
          'flex max-w-[80%] flex-col gap-1',
          isOwn ? 'items-end' : 'items-start',
        )}
      >
        <div
          className={clsx(
            !bare && 'overflow-hidden rounded-2xl',
            !bare && (isOwn ? 'rounded-br-sm' : 'rounded-bl-sm'),
          )}
        >
          {children}
        </div>
        <BubbleMeta message={message} isOwn={isOwn} time={time} />
      </div>
    </div>
  );
}

function BubbleMeta({
  message,
  isOwn,
  time,
}: {
  message: ChatMessage;
  isOwn: boolean;
  time: string;
}) {
  const clientMsg = message as Partial<ClientChatMessage>;
  const isSending = clientMsg.sendStatus === MsgSendStatus.sending;
  const isFailed = clientMsg.sendStatus === MsgSendStatus.fail;

  return (
    <div
      className={clsx(
        'flex items-center gap-1.5 text-[10px]',
        isOwn ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      <span className="text-content-3">{time}</span>
      {isSending ? <span className="text-content-3">Sending…</span> : null}
      {isFailed ? (
        <span
          className="text-red-400"
          title={clientMsg.lastSendErrorMsg ?? undefined}
        >
          Failed
        </span>
      ) : null}
    </div>
  );
}
